/**
 * B403 #37 — the expired-cart self-heal, as ONE seam.
 *
 * A remembered cartId the server no longer holds (TTL'd, abandoned
 * elsewhere) must not trap the buyer in a refusal loop: the wire's exact
 * "No such cart" refusal retires the stored id and the SAME press replays
 * once on a fresh cart. Only that refusal heals — every other refusal
 * (and a second "No such cart" on the fresh cart) still surfaces.
 *
 * The orchestration lives here rather than inline in App.tsx so the
 * composed no-such-cart-heals walls (test/cartHeal.test.ts) drive the
 * very unit the container spends — a wall over a copy is not a wall.
 */
import { ApiError } from "./api.js";

/** The one refusal that self-heals: the wire's own sentence, exactly. */
export function isNoSuchCart(error: unknown): boolean {
  return error instanceof ApiError && error.message === "No such cart";
}

export interface CartHealDeps {
  /** Open (or re-open) a cart, returning its id — App.tsx's openCart. */
  readonly open: () => Promise<string>;
  /** Retire the remembered id — session.clearCartId. */
  readonly clear: () => void;
}

/**
 * Run `press` against the open cart; on the exact "No such cart" refusal,
 * retire the stored id and replay the SAME press once on a fresh cart.
 * The heal replays ONCE — a refusal on the fresh cart surfaces.
 */
export async function runWithCartHeal(
  deps: CartHealDeps,
  press: (cartId: string) => Promise<void>,
): Promise<void> {
  const first = await deps.open();
  try {
    await press(first);
  } catch (error) {
    if (isNoSuchCart(error) === false) throw error;
    deps.clear();
    const fresh = await deps.open();
    await press(fresh);
  }
}
